import { APIURL } from "./config";

export interface RoleOptionWeight {
  questionId: string;
  answerId: string;
  weight: number;
}

/** Points for a free-text question: earned in full if answered, 0 if left empty. */
export interface RoleTextQuestionWeight {
  questionId: string;
  weight: number;
}

export interface RoleApplicationScoring {
  /** Score (%) at or above which a candidate is Relevant. */
  relevantThreshold: number;
  /** Score (%) at or above which a candidate is Good. */
  goodThreshold: number;
  weights: RoleOptionWeight[];
  textWeights: RoleTextQuestionWeight[];
}

/** What a free-text question is pre-filled with in the role form until the recruiter changes it. */
export const DEFAULT_TEXT_QUESTION_WEIGHT = 5;

// ── Per-candidate breakdown ─────────────────────────────────────────────────

export type QuestionScoreStatus = "Scored" | "NotShown" | "NotScored";

export interface AnswerScoreBreakdown {
  text: string;
  /** Points this answer is worth; null when it carries no weight. */
  weight: number | null;
}

export interface QuestionScoreBreakdown {
  questionId: string;
  questionText: string;
  inputType: "options" | "text" | "number";
  isMultiple: boolean;
  isFollowUp: boolean;
  status: QuestionScoreStatus;
  answers: AnswerScoreBreakdown[];
  pointsEarned: number;
  pointsPossible: number;
}

export interface ApplicationScoreBreakdown {
  score: number | null;
  category: ApplicationCategory;
  pointsEarned: number;
  pointsPossible: number;
  relevantThreshold: number;
  goodThreshold: number;
  questions: QuestionScoreBreakdown[];
}

export const GetCandidateApplicationBreakdown = async (
  roleId: string,
  candidateId: string,
  token: string
): Promise<ApplicationScoreBreakdown> => {
  const res = await fetch(
    `${APIURL}/Roles/${roleId}/application-scoring/candidates/${candidateId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) throw new Error((await res.text()) || "Failed to load the grading breakdown");

  return res.json();
};

/** Mirrors ApplicationCategories on the server. */
export type ApplicationCategory = "NotScored" | "NotRelevant" | "Relevant" | "Good";

export const APPLICATION_CATEGORY_LABELS: Record<ApplicationCategory, string> = {
  NotScored: "Not scored",
  NotRelevant: "Not relevant",
  Relevant: "Relevant",
  Good: "Good",
};

export const APPLICATION_CATEGORY_STYLES: Record<ApplicationCategory, string> = {
  NotScored: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  NotRelevant: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  Relevant: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  Good: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
};

/** Same values the server falls back to when a role's thresholds were never set. */
export const DEFAULT_RELEVANT_THRESHOLD = 40;
export const DEFAULT_GOOD_THRESHOLD = 70;

export const GetRoleApplicationScoring = async (
  roleId: string,
  token: string
): Promise<RoleApplicationScoring> => {
  const res = await fetch(`${APIURL}/Roles/${roleId}/application-scoring`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) throw new Error((await res.text()) || "Failed to load application scoring");

  return res.json();
};

/**
 * Replace-all save. The server re-scores every existing applicant of the role before it
 * responds, and returns how many it re-scored.
 */
export const SaveRoleApplicationScoring = async (
  roleId: string,
  scoring: RoleApplicationScoring,
  token: string
): Promise<{ candidatesRecomputed: number }> => {
  const res = await fetch(`${APIURL}/Roles/${roleId}/application-scoring`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(scoring),
  });

  if (!res.ok) throw new Error((await res.text()) || "Failed to save application scoring");

  return res.json();
};

/**
 * Mirrors RoleQuestionTags.IsOptions on the server and getRoleInputTypeFromTags in the careers
 * form: a missing roleInputType tag means options. Only options questions can carry weights.
 */
export const isOptionsRoleQuestion = (tags?: string | null): boolean => {
  if (!tags) return true;
  const tag = tags
    .split(",")
    .map((t) => t.trim())
    .find((t) => t.toLowerCase().startsWith("roleinputtype:"));
  const value = tag?.slice(tag.indexOf(":") + 1).trim().toLowerCase();
  return value !== "text" && value !== "number";
};

/**
 * Whether a role question is a follow-up, by the careers form's own rule: it has trigger options,
 * i.e. a whichAnswerFollowUp or a "followUpOptions:<id>|<id>" tag. Mirrors
 * RoleQuestionTags.FollowUpTriggerIds on the server.
 */
export const isFollowUpRoleQuestion = (q: {
  whichAnswerFollowUp?: string | null;
  tags?: string | null;
}): boolean => {
  if (q.whichAnswerFollowUp) return true;
  const tag = (q.tags ?? "")
    .split(",")
    .map((t) => t.trim())
    .find((t) => t.toLowerCase().startsWith("followupoptions:"));
  return !!tag && tag.slice(tag.indexOf(":") + 1).split("|").some((id) => id.trim().length > 0);
};

/** Mirrors RoleQuestionTags.InputType on the server. */
export const isTextRoleQuestion = (tags?: string | null): boolean => {
  if (!tags) return false;
  const tag = tags
    .split(",")
    .map((t) => t.trim())
    .find((t) => t.toLowerCase().startsWith("roleinputtype:"));
  return tag?.slice(tag.indexOf(":") + 1).trim().toLowerCase() === "text";
};

export const isMultipleRoleQuestion = (tags?: string | null): boolean => {
  if (!tags) return false;
  const tag = tags
    .split(",")
    .map((t) => t.trim())
    .find((t) => t.toLowerCase().startsWith("roleoptionmode:"));
  return tag?.slice(tag.indexOf(":") + 1).trim().toLowerCase() === "multiple";
};
